import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export type ArtifactType = "email" | "contract";
export type ArtifactStatus = "draft" | "sending" | "sent" | "failed";

export interface EmailArtifactPayload {
  to: string;
  subject: string;
  htmlBody: string;
  prospectId?: string | null;
  attachments?: Array<{ name: string; mimeType: string; data: string }>;
}

export interface ContractArtifactPayload {
  title: string;
  htmlBody: string;
  recipient?: string | null;
}

export interface Artifact {
  id: string;
  type: ArtifactType;
  title: string;
  createdAt: number;
  updatedAt: number;
  status: ArtifactStatus;
  conversationId?: string | null;
  email?: EmailArtifactPayload;
  contract?: ContractArtifactPayload;
  errorMessage?: string | null;
}

interface PushDraftInput {
  type: ArtifactType;
  title: string;
  conversationId?: string | null;
  email?: EmailArtifactPayload;
  contract?: ContractArtifactPayload;
}

interface ArtifactsContextValue {
  artifacts: Artifact[];
  activeId: string | null;
  isOpen: boolean;
  reviewMode: boolean;
  setReviewMode: (v: boolean) => void;
  open: (id?: string) => void;
  close: () => void;
  toggle: () => void;
  pushDraft: (input: PushDraftInput) => string;
  updateArtifact: (id: string, patch: Partial<Omit<Artifact, "id">>) => void;
  removeArtifact: (id: string) => void;
  setActive: (id: string | null) => void;
}

const ArtifactsContext = createContext<ArtifactsContextValue | null>(null);

const REVIEW_MODE_KEY = "thiia.artifacts.reviewMode";

// Tiny global bus so non-React code (chat tools) can push without prop drilling.
type BusListener = (input: PushDraftInput) => string;
const busListeners = new Set<BusListener>();

export function pushArtifactFromAnywhere(input: PushDraftInput): string | null {
  for (const listener of busListeners) {
    try {
      return listener(input);
    } catch (e) {
      console.error("[artifacts] bus listener error", e);
    }
  }
  return null;
}

export function isArtifactReviewMode(): boolean {
  try {
    const v = localStorage.getItem(REVIEW_MODE_KEY);
    return v === null ? true : v === "true";
  } catch {
    return true;
  }
}

export function openArtifactsPanel(id?: string) {
  try {
    window.dispatchEvent(new CustomEvent("thiia:artifacts:open", { detail: { id } }));
  } catch {}
}

export function ArtifactsProvider({ children }: { children: ReactNode }) {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [reviewMode, setReviewModeState] = useState<boolean>(() => isArtifactReviewMode());
  const counter = useRef(0);

  const setReviewMode = useCallback((v: boolean) => {
    setReviewModeState(v);
    try { localStorage.setItem(REVIEW_MODE_KEY, v ? "true" : "false"); } catch {}
  }, []);

  const pushDraft = useCallback((input: PushDraftInput): string => {
    counter.current += 1;
    const id = `art_${Date.now()}_${counter.current}`;
    const now = Date.now();
    const artifact: Artifact = {
      id,
      type: input.type,
      title: input.title || (input.type === "email" ? "Brouillon email" : "Document"),
      createdAt: now,
      updatedAt: now,
      status: "draft",
      conversationId: input.conversationId ?? null,
      email: input.email,
      contract: input.contract,
    };
    setArtifacts((prev) => [artifact, ...prev]);
    setActiveId(id);
    setIsOpen(true);
    return id;
  }, []);

  const updateArtifact = useCallback((id: string, patch: Partial<Omit<Artifact, "id">>) => {
    setArtifacts((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              ...patch,
              email: patch.email ? { ...(a.email || ({} as EmailArtifactPayload)), ...patch.email } : a.email,
              contract: patch.contract ? { ...(a.contract || ({} as ContractArtifactPayload)), ...patch.contract } : a.contract,
              updatedAt: Date.now(),
            }
          : a
      )
    );
  }, []);

  const removeArtifact = useCallback((id: string) => {
    setArtifacts((prev) => prev.filter((a) => a.id !== id));
    setActiveId((cur) => (cur === id ? null : cur));
  }, []);

  const open = useCallback((id?: string) => {
    if (id) setActiveId(id);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const setActive = useCallback((id: string | null) => setActiveId(id), []);

  // Register bus listener
  useEffect(() => {
    const listener: BusListener = (input) => pushDraft(input);
    busListeners.add(listener);
    return () => {
      busListeners.delete(listener);
    };
  }, [pushDraft]);

  // Window event for opening
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      open(detail?.id);
    };
    window.addEventListener("thiia:artifacts:open", handler);
    return () => window.removeEventListener("thiia:artifacts:open", handler);
  }, [open]);

  const value = useMemo<ArtifactsContextValue>(
    () => ({
      artifacts,
      activeId,
      isOpen,
      reviewMode,
      setReviewMode,
      open,
      close,
      toggle,
      pushDraft,
      updateArtifact,
      removeArtifact,
      setActive,
    }),
    [artifacts, activeId, isOpen, reviewMode, setReviewMode, open, close, toggle, pushDraft, updateArtifact, removeArtifact, setActive]
  );

  return <ArtifactsContext.Provider value={value}>{children}</ArtifactsContext.Provider>;
}

export function useArtifacts(): ArtifactsContextValue {
  const ctx = useContext(ArtifactsContext);
  if (!ctx) {
    // Safe no-op fallback so components don't crash if rendered outside provider (tests).
    return {
      artifacts: [],
      activeId: null,
      isOpen: false,
      reviewMode: true,
      setReviewMode: () => {},
      open: () => {},
      close: () => {},
      toggle: () => {},
      pushDraft: () => "",
      updateArtifact: () => {},
      removeArtifact: () => {},
      setActive: () => {},
    };
  }
  return ctx;
}
